import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Search, Loader2, Plus, MapPin, Phone, Pencil, Trash2 } from "lucide-react";
import { AddressPicker, composeAddress } from "@/components/AddressPicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const field = "mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#0B192C]";
const lbl = "text-xs font-semibold uppercase tracking-wider text-slate-500";
const EMPTY = { name: "", trading_name: "", business_address: "", delivery_address: "", street: "", suburb: "", state: "NSW", postcode: "", place_id: "", contact_person: "", phone: "", email: "", latitude: "", longitude: "", gps_radius: 100, notes: "", active: true };

export default function Customers() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const del = (c) => setConfirm({
    title: `Delete customer ${c.name}?`,
    description: "This permanently removes the customer. Customers with existing orders can't be deleted — remove those orders first.",
    confirmLabel: "Delete", danger: true,
    onConfirm: async () => {
      try { await api.delete(`/customers/${c.id}`); qc.invalidateQueries({ queryKey: ["customers"] }); toast.success("Customer deleted"); }
      catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    },
  });

  const { data: customers = [], isLoading } = useQuery({
    queryKey: ["customers", q],
    queryFn: async () => (await api.get("/customers", { params: { q: q || undefined } })).data,
  });

  return (
    <div className="animate-slide-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-head text-2xl font-extrabold text-[#0B192C]">Customers</h1><p className="text-sm text-slate-500">{customers.length} customers</p></div>
        <button onClick={() => setEdit(EMPTY)} data-testid="add-customer-btn" className="flex items-center gap-2 rounded-xl bg-[#FF6500] px-4 py-2.5 text-sm font-bold text-white tap-shrink hover:bg-[#E05A00]"><Plus size={18} /> Add Customer</button>
      </div>

      <div className="relative mt-4 max-w-md">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="customers-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers…" className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-[#0B192C]" />
      </div>

      {isLoading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#FF6500]" /></div> : (
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {customers.map((c) => (
            <div key={c.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`customer-card-${c.id}`}>
              <div className="flex items-start justify-between">
                <div><div className="font-head font-bold text-[#0B192C]">{c.name}</div>{c.trading_name && <div className="text-xs text-slate-400">{c.trading_name}</div>}</div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setEdit({ ...c, latitude: c.latitude ?? "", longitude: c.longitude ?? "" })} data-testid={`edit-customer-${c.id}`} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><Pencil size={15} /></button>
                  <button onClick={() => del(c)} data-testid={`delete-customer-${c.id}`} className="rounded-lg p-1.5 text-rose-500 hover:bg-rose-50"><Trash2 size={15} /></button>
                </div>
              </div>
              <div className="mt-2 flex items-start gap-1.5 text-sm text-slate-500"><MapPin size={14} className="mt-0.5 shrink-0" /> {c.delivery_address}</div>
              {c.phone && <div className="mt-1 flex items-center gap-1.5 text-sm text-slate-500"><Phone size={14} /> {c.contact_person} · {c.phone}</div>}
              <div className="mt-2 flex items-center gap-2 text-xs">
                {c.latitude != null ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">GPS set · {c.gps_radius}m</span> : <span className="rounded-full bg-amber-50 px-2 py-0.5 font-semibold text-amber-700">No GPS</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {edit && <CustomerDialog initial={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); qc.invalidateQueries({ queryKey: ["customers"] }); }} />}
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function CustomerDialog({ initial, onClose, onDone }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const isEdit = !!f.id;
  const hasParts = f.street || f.suburb || f.postcode;
  const address = hasParts ? composeAddress(f) : f.delivery_address;

  const save = async () => {
    if (!f.name || !address) return toast.error("Name and delivery address required");
    if (hasParts && !f.suburb) return toast.error("Suburb is required");
    setSaving(true);
    const payload = { ...f, delivery_address: address, latitude: f.latitude === "" ? null : parseFloat(f.latitude), longitude: f.longitude === "" ? null : parseFloat(f.longitude), gps_radius: parseInt(f.gps_radius || 100) };
    try {
      if (isEdit) await api.put(`/customers/${f.id}`, payload);
      else await api.post("/customers", payload);
      toast.success(`Customer ${isEdit ? "updated" : "created"}`);
      onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto scrollbar-thin" data-testid="customer-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Edit" : "Add"} Customer</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Customer Name *</label><input data-testid="customer-name-input" value={f.name} onChange={(e) => set("name", e.target.value)} className={field} /></div>
            <div><label className={lbl}>Trading Name</label><input value={f.trading_name} onChange={(e) => set("trading_name", e.target.value)} className={field} /></div>
          </div>
          <div>
            <div className="mb-1 flex items-baseline justify-between"><label className={lbl}>Delivery Address *</label>
              {address && <span className="truncate pl-3 text-xs text-slate-500" data-testid="customer-address-preview">{address}</span>}</div>
            <AddressPicker value={f} onChange={setF} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Contact Person</label><input value={f.contact_person} onChange={(e) => set("contact_person", e.target.value)} className={field} /></div>
            <div><label className={lbl}>Phone</label><input value={f.phone} onChange={(e) => set("phone", e.target.value)} className={field} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Business Address</label><input value={f.business_address || ""} onChange={(e) => set("business_address", e.target.value)} placeholder="If different" className={field} /></div>
            <div><label className={lbl}>GPS Radius (m)</label><input type="number" value={f.gps_radius} onChange={(e) => set("gps_radius", e.target.value)} className={field} /></div>
          </div>
          <div><label className={lbl}>Notes</label><textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className={field} /></div>
          <button onClick={save} disabled={saving} data-testid="save-customer-btn" className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#0B192C] py-3 font-bold text-white tap-shrink disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={18} /> : "Save Customer"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
